/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    AfterViewInit,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import {
    IAviDataGridConfig,
    IAviDataGridRow,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import * as l10n from './log-policy-grid.l10n';

import './log-policy-grid.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

type TL7FeaturedPolicy = Record<string, any> | null;

/**
 * @description
 *     A grid component for showing which L7 featured parts (aka policies) are logged for the VS.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'log-policy-grid',
    templateUrl: './log-policy-grid.component.html',
})
export class LogPolicyGridComponent implements OnInit, AfterViewInit {
    @Input()
    public ssl ?: TL7FeaturedPolicy;

    @Input()
    public hasAuthenticationLogs = false;

    @Input()
    public icapLog ?: TL7FeaturedPolicy;

    @Input()
    public wafLog ?: TL7FeaturedPolicy;

    @Input()
    public botManagementLog ?: TL7FeaturedPolicy;

    @Input()
    public datascriptInfo ?: TL7FeaturedPolicy;

    /**
     * Show the grid in compact mode, typically used in a list row expander.
     */
    @Input()
    public compactMode = false;

    @ViewChild('policiesAppliedFieldTemplateRef')
    public readonly policiesAppliedFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Get global keys from source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * List of L7 featured parts that are logged for the VS. Each section can be also referred to as
     * a "policy".
     */
    public policyGridRows: IAviDataGridRow[];

    /**
     * Configuration for the grid to show what policies are applied.
     */
    public policyGridConfig: IAviDataGridConfig;

    constructor(
        private readonly l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.policyGridRows = [
            {
                label: this.l10nService.getMessage(this.l10nKeys.sslLabel),
                applied: Boolean(this.ssl),
            },
            {
                label: this.l10nService.getMessage(this.globalL10nKeys.authenticationLabel),
                applied: this.hasAuthenticationLogs,
            },
            {
                label: this.l10nService.getMessage(this.l10nKeys.icapLabel),
                applied: Boolean(this.icapLog),
            },
            {
                label: this.l10nService.getMessage(this.globalL10nKeys.wafLabel),
                applied: Boolean(this.wafLog),
            },
            {
                label: this.l10nService.getMessage(this.l10nKeys.botManagementLabel),
                applied: Boolean(this.botManagementLog),
            },
            {
                label: this.l10nService.getMessage(this.l10nKeys.datascriptLabel),
                applied: Boolean(this.datascriptInfo),
            },
        ];
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.policyGridConfig = {
            fields: [
                {
                    label: this.l10nService.getMessage(this.l10nKeys.policiesAppliedLabel),
                    id: 'policies-applied',
                    templateRef: this.policiesAppliedFieldTemplateRef,
                },
            ],
            layout: {
                hidePagination: true,
                hideSearch: true,
                hideCheckboxes: true,
                compact: this.compactMode,
                showFooter: !this.compactMode,
            },
        };
    }
}
